import { playButtonSound } from '@gamiphy/loyalty-station-desktop/sounds';
import { PopupsStore } from '@gamiphy/loyalty-station-desktop/store/popups';
import { SdkStore } from '@gamiphy/loyalty-station-desktop/store/sdk';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { Pages, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { useDisplayStringValue, useTranslation } from '@gamiphy/react-i18n';
import { copyTextToClipboard } from '@gamiphy/utils/general';
import { Button, Grid } from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';
import {
  PopupCtoDtoActionEnum,
  PopupDto,
} from '@coretava/api/core-service';
import { DisplayString } from '@coretava/i18n';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { POPUP_TYPE_OPEN_SAME_PAGE, PopupsTypesMap } from './constants';

const PopupButton: React.FC<{
  popup: PopupDto,
  fontSize: string,
  padding: string,
  borderRadius: string,
  discountCode?: string
  closePopup: () => void
}> = ({
  popup,
  fontSize,
  padding,
  borderRadius,
  discountCode,
  closePopup,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const buttonText = useDisplayStringValue(popup.cta.text as DisplayString);

  const onClick = React.useCallback(() => {
    void playButtonSound();
    void PopupsStore.api.popupControllerClick(AppStore.app.id, popup.id);

    if (POPUP_TYPE_OPEN_SAME_PAGE.some((type) => type === popup.predefinedType)) {
      UiStore.open();
      UiStore.setPage(Pages.home);
      UiStore.setTab(PopupsTypesMap[popup.predefinedType]);
    } else if (popup.cta.action === PopupCtoDtoActionEnum.Link) {
      window.open(popup.cta.href ?? undefined, '_blank');
    } else if (popup.cta.action === PopupCtoDtoActionEnum.CopyCode && popup.cta.text) {
      void copyTextToClipboard(discountCode ?? '');
      setCopied(true);
    } else if (popup.cta.action === PopupCtoDtoActionEnum.Login) {
      if (ThemeStore.theme.enableSmsFlow) {
        PopupsStore.openJoinProgram();
      } else {
        void SdkStore.goToAuth(false);
      }
    } else if (popup.cta.action === PopupCtoDtoActionEnum.Close) {
      closePopup();
    }
  }, [popup,
    UserStore.isLoggedIn,
    ThemeStore.theme.enableSmsFlow,
    PopupsStore.api,
    closePopup,
    discountCode]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    popup.cta.action === PopupCtoDtoActionEnum.CopyCode ? (
      <Grid container>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          size={6}
          sx={{
            backgroundColor: '#f0f0f0',
            borderBottomLeftRadius: borderRadius,
            borderTopLeftRadius: borderRadius,
          }}
        >
          {discountCode ?? t('code', 'Code')}
        </Grid>
        <Grid size={6}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            sx={{
              backgroundColor: popup.colors.btnBg,
              color: popup.colors.btnText,
              zIndex: 1000,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              fontSize,
              p: padding,
              lineHeight: '150%',
            }}
            onClick={onClick}
          >
            {
              (() => {
                switch (true) {
                  case copied:
                    return t('wallet.unlocked.code.copied', 'Code Copied');
                  default:
                    return buttonText;
                }
              })()
            }
          </Button>
        </Grid>
      </Grid>
    ) : (
      <Button
        variant="contained"
        color="secondary"
        fullWidth
        sx={{
          backgroundColor: popup.colors.btnBg,
          color: popup.colors.btnText,
          zIndex: 1000,
          fontSize,
          p: padding,
          borderRadius,
          lineHeight: '150%',
          ...popup.styles.cta,
        }}
        onClick={onClick}
      >
        {buttonText}
      </Button>
    )
  );
};

export default observer(PopupButton);
